/**
 * 新闻舱首页「官网栏目」(1009 二期):建 / 改 / 停栏目;每个栏目的排期条目(日期、选题可改、状态、操作)。
 *
 * ★ 条目操作:改题 / 删除 / 立即生成 / 去审(进预览页)/ 确认(按计划发布)。确认项(没评分 / HIGH 风险 / 行业禁入)没在这里确认时,
 *   后端回 409,这里提示去预览页审了再确认。
 * ★ 选题不扣费;生成按新闻舱写稿扣费;未审稿不会发到官网。
 */
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Loader2, Plus } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { toast } from "@/shared/hooks/use-toast";
import { errorText, errorToast } from "@/shared/lib/apiErrors";
import { ApiError } from "@/shared/api";

import {
  reporterColumnService,
  type ColumnDetail,
  type ColumnItem,
  type SiteColumn,
} from "../api/reporterColumnService";
import { GENRE_LABEL } from "../model/labels";
import {
  cadenceText,
  COLUMN_ITEM_LABEL,
  COLUMNS_KEY,
  itemActions,
  plannedText,
} from "../model/siteColumn";
import { siteLabel } from "../model/sitePublication";
import { SiteColumnFormDialog } from "./SiteColumnFormDialog";

const ITEM_TONE: Record<string, string> = {
  PENDING: "border-border text-muted-foreground",
  GENERATING: "border-primary/40 text-primary",
  REVIEW: "border-warning/50 bg-warning/10 text-warning",
  CONFIRMED: "border-primary/40 bg-primary/10 text-primary",
  PUBLISHED: "border-success/40 bg-success/10 text-success",
  FAILED: "border-destructive/40 bg-destructive/10 text-destructive",
  SKIPPED: "border-border bg-muted text-muted-foreground",
};

export function SiteColumnsSection() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SiteColumn | null | "new">(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const listQ = useQuery({ queryKey: COLUMNS_KEY, queryFn: () => reporterColumnService.list() });
  const columns = listQ.data ?? [];

  const refresh = () => void qc.invalidateQueries({ queryKey: COLUMNS_KEY });

  return (
    <section data-site-columns="">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <span className="h-3.5 w-[3px] self-center rounded-sm bg-primary" />
        <h2 className="text-sm font-semibold">官网栏目</h2>
        <span className="text-xs text-muted-foreground">
          定期给官网新闻栏发文章:AI 出选题 → 提前写好 → 你审过才发
        </span>
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => setEditing("new")}
          data-site-column-new=""
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          新建栏目
        </Button>
      </div>
      {listQ.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {listQ.isError && <p className="text-xs text-destructive">{errorText(listQ.error)}</p>}
      {!listQ.isLoading && columns.length === 0 && !listQ.isError && (
        <p className="text-xs text-muted-foreground">
          还没有栏目。建一个,比如「每周一三五 9 点发一篇行业观察」。
        </p>
      )}
      <div className="space-y-3">
        {columns.map((c) => (
          <ColumnCard
            key={c.id}
            column={c}
            open={openId === c.id}
            onToggle={() => setOpenId(openId === c.id ? null : c.id)}
            onEdit={() => setEditing(c)}
            onChanged={refresh}
          />
        ))}
      </div>
      {editing && (
        <SiteColumnFormDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(d) => {
            refresh();
            qc.setQueryData(["reporter-site-column", d.column.id], d);
            setOpenId(d.column.id);
            toast({ title: editing === "new" ? "栏目已建好" : "已保存" });
          }}
        />
      )}
    </section>
  );
}

function ColumnCard({
  column: c,
  open,
  onToggle,
  onEdit,
  onChanged,
}: {
  column: SiteColumn;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const key = ["reporter-site-column", c.id];
  const detailQ = useQuery({
    queryKey: key,
    queryFn: () => reporterColumnService.detail(c.id),
    enabled: open,
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((i) => i.status === "GENERATING") ? 10_000 : false,
  });

  const act = async (fn: () => Promise<ColumnDetail | void>, ok: string) => {
    setBusy(true);
    try {
      const d = await fn();
      if (d) qc.setQueryData(key, d);
      else await qc.invalidateQueries({ queryKey: key });
      onChanged();
      toast({ title: ok });
    } catch (e) {
      toast(errorToast(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-[calc(var(--radius)+2px)] border bg-card p-3" data-site-column={c.id}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="text-left text-sm font-semibold hover:underline"
          onClick={onToggle}
        >
          {c.name}
        </button>
        <span className="text-xs text-muted-foreground">
          <span data-site-column-site="">
            {c.siteName == null ? "站点已删除" : siteLabel(c.siteName, c.siteUrl)}
          </span>{" "}
          · {GENRE_LABEL[c.genre as keyof typeof GENRE_LABEL] ?? c.genre}
        </span>
        {!c.enabled && (
          <span className="rounded-full border px-1.5 text-2xs text-muted-foreground">已停用</span>
        )}
        {c.counts.REVIEW > 0 && (
          <span className="rounded-full border border-warning/50 bg-warning/10 px-1.5 text-2xs text-warning">
            待审 {c.counts.REVIEW}
          </span>
        )}
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit}>
            改
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={busy}
            onClick={() =>
              void act(
                () => reporterColumnService.setEnabled(c.id, !c.enabled),
                c.enabled ? "栏目已停用" : "栏目已启用"
              )
            }
          >
            {c.enabled ? "停用" : "启用"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-destructive hover:text-destructive"
            disabled={busy}
            onClick={async () => {
              const ok = await confirm({
                title: `删除栏目「${c.name}」?`,
                description: "还没生成的排期一起删;已写好的稿件和已排的发布不动。",
                confirmText: "删除",
                variant: "destructive",
              });
              if (ok) await act(() => reporterColumnService.remove(c.id), "栏目已删除");
            }}
          >
            删除
          </Button>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground" data-site-column-cadence="">
        <CalendarClock className="mr-1 inline h-3.5 w-3.5" />
        {cadenceText(c)}
        {c.nextPlannedAt ? ` · 下一期 ${plannedText(c.nextPlannedAt)}` : ""}
      </p>
      {c.lastTopicError && <p className="mt-1 text-xs text-warning">{c.lastTopicError}</p>}
      {open && (
        <div className="mt-3 border-t pt-2">
          {detailQ.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {detailQ.data && (
            <ItemsList
              items={detailQ.data.items}
              busy={busy}
              onRoll={() => void act(() => reporterColumnService.roll(c.id), "已补选题")}
              act={act}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ItemsList({
  items,
  busy,
  onRoll,
  act,
}: {
  items: ColumnItem[];
  busy: boolean;
  onRoll: () => void;
  act: (fn: () => Promise<ColumnDetail | void>, ok: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [editId, setEditId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [angle, setAngle] = useState("");

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-xs">
        <span className="font-semibold">排期</span>
        <span className="text-muted-foreground">{items.length} 期</span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto h-6 text-2xs"
          disabled={busy}
          onClick={onRoll}
        >
          补选题
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          还没有排期(选题生成中,或节奏 / 上限排不进)。
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((i) => {
            const a = itemActions(i);
            return (
              <li
                key={i.id}
                className="rounded-md border border-border/70 px-2.5 py-1.5 text-xs"
                data-site-column-item={i.id}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-28 shrink-0 font-mono text-2xs text-muted-foreground">
                    {plannedText(i.plannedAt)}
                  </span>
                  {editId === i.id ? (
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <input
                        aria-label="选题标题"
                        className="h-7 rounded border border-input bg-background px-1.5"
                        value={title}
                        maxLength={64}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                      <input
                        aria-label="选题角度"
                        className="h-7 rounded border border-input bg-background px-1.5"
                        value={angle}
                        maxLength={255}
                        onChange={(e) => setAngle(e.target.value)}
                      />
                    </span>
                  ) : (
                    <span className="min-w-0 flex-1">
                      <b className="block truncate">{i.title}</b>
                      {i.angle && (
                        <span className="block truncate text-muted-foreground">{i.angle}</span>
                      )}
                    </span>
                  )}
                  <span
                    className={cn("rounded-full border px-1.5 py-px text-2xs", ITEM_TONE[i.status])}
                    data-site-column-item-status={i.status}
                  >
                    {COLUMN_ITEM_LABEL[i.status] ?? i.status}
                  </span>
                </div>
                {i.lastError && <p className="mt-1 text-destructive">{i.lastError}</p>}
                {i.warning && (
                  <p
                    className="mt-1 rounded border border-warning/50 bg-warning/10 px-1.5 py-1 text-warning"
                    data-site-column-item-warning=""
                  >
                    {i.warning}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {editId === i.id ? (
                    <>
                      <Act
                        disabled={busy || !title.trim()}
                        onClick={() =>
                          act(
                            () =>
                              reporterColumnService.updateTopic(i.id, {
                                title: title.trim(),
                                angle: angle.trim() || null,
                                keywords: i.keywords,
                              }),
                            "选题已改"
                          ).then(() => setEditId(null))
                        }
                      >
                        保存
                      </Act>
                      <Act onClick={() => setEditId(null)}>取消</Act>
                    </>
                  ) : (
                    a.editTopic && (
                      <Act
                        disabled={busy}
                        onClick={() => {
                          setEditId(i.id);
                          setTitle(i.title);
                          setAngle(i.angle ?? "");
                        }}
                      >
                        改题
                      </Act>
                    )
                  )}
                  {a.generate && (
                    <Act
                      disabled={busy}
                      onClick={async () => {
                        const ok = await confirm({
                          title: "现在就写这一期?",
                          description: "按新闻舱写稿扣积分;写好后进「待审」,你确认了才会发到官网。",
                          confirmText: "开始写",
                        });
                        if (ok) await act(() => reporterColumnService.generate(i.id), "已开始写稿");
                      }}
                    >
                      立即生成
                    </Act>
                  )}
                  {a.review && i.draftId && (
                    <Act
                      onClick={() => navigate(`/ai-jizhe/preview/${i.draftId}?site=${i.siteId}`)}
                    >
                      去审
                    </Act>
                  )}
                  {a.confirm && (
                    <Act
                      disabled={busy}
                      primary
                      onClick={async () => {
                        try {
                          await reporterColumnService.confirm(i.id, false);
                          await act(async () => undefined, "已确认,按计划发布");
                        } catch (e) {
                          if (e instanceof ApiError && e.errorCode === "REPORTER_EXPORT_GATED") {
                            toast({
                              title: "这篇有需要确认的提醒",
                              description: "点「去审」在预览页看过、确认后再发布",
                            });
                          } else {
                            toast(errorToast(e));
                          }
                        }
                      }}
                    >
                      确认
                    </Act>
                  )}
                  {a.remove && (
                    <Act
                      disabled={busy}
                      danger
                      onClick={async () => {
                        const ok = await confirm({
                          title: "删掉这一期?",
                          description: i.draftId ? "已写好的稿件留在新闻舱,不会删。" : "只删排期。",
                          confirmText: "删除",
                          variant: "destructive",
                        });
                        if (ok)
                          await act(() => reporterColumnService.removeItem(i.id), "已删除这一期");
                      }}
                    >
                      删除
                    </Act>
                  )}
                  {i.siteUrl && i.status === "PUBLISHED" && (
                    <a
                      href={i.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      打开官网
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Act({
  children,
  onClick,
  disabled,
  primary,
  danger,
}: {
  children: ReactNode;
  onClick: () => unknown;
  disabled?: boolean;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <Button
      size="sm"
      variant={primary ? "default" : "outline"}
      className={cn("h-6 px-2 text-2xs", danger && "text-destructive hover:text-destructive")}
      disabled={disabled}
      onClick={() => void onClick()}
    >
      {children}
    </Button>
  );
}
